import { fetchAPI } from '../../../services/apiClient';

// ========================
// Types
// ========================

export interface AsignacionLoteria {
  nombre_loteria: string;
  cantidad_asignada: number;
  cantidad_final: number;
  valor_unitario: string;
  vendido?: number;
  total_vendido_item?: string;
}

export interface CierreCajaLoteriaInput {
  vendedor_id: string;
  fecha: string;
  asignaciones: AsignacionLoteria[];
  total_efectivo: string;
  total_sinpe: string;
  notas?: string;
}

export interface CierreCajaLoteriaResult {
  id: string;
  vendedor_id: string;
  fecha: string;
  total_asignado: string;
  total_vendido: string;
  total_efectivo: string;
  total_sinpe: string;
  dinero_esperado: string;
  dinero_reportado: string;
  diferencia: string;
  estado_diferencia: 'CUADRA' | 'SOBRA' | 'FALTA';
  asignaciones: AsignacionLoteria[];
}

export interface CierreLoteriasHistorico {
  id: string;
  vendedor_id: string;
  local_id: string;
  fecha: string;
  total_asignado: string;
  total_vendido: string;
  total_efectivo: string;
  total_sinpe: string;
  dinero_esperado: string;
  dinero_reportado: string;
  diferencia: string;
  estado_diferencia: 'CUADRA' | 'SOBRA' | 'FALTA';
  anulado: boolean;
  created_at: string | null;
}

export interface DetalleCierreLoterias extends CierreLoteriasHistorico {
  notas: string | null;
  anulado: boolean;
  anulado_por: string | null;
  anulado_at: string | null;
  motivo_anulacion: string | null;
  asignaciones: AsignacionLoteria[];
}

export interface EstadoCierreLoterias {
  vendedor_id: string;
  fecha: string;
  cerrada: boolean;
  cierre_id: string | null;
}

// ========================
// API calls
// ========================

export async function cerrarCajaLoteria(
  data: CierreCajaLoteriaInput
): Promise<CierreCajaLoteriaResult> {
  return fetchAPI<CierreCajaLoteriaResult>('/caja-loteria/cerrar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function getCierresLoterias(
  vendedorId?: string,
  limit: number = 30,
  incluirAnulados: boolean = true
): Promise<CierreLoteriasHistorico[]> {
  let url = `/caja-loteria/cierres?limit=${limit}&incluir_anulados=${incluirAnulados}`;
  if (vendedorId) url += `&vendedor_id=${vendedorId}`;
  return fetchAPI<CierreLoteriasHistorico[]>(url);
}

export async function getDetalleCierreLoterias(
  cierreId: string
): Promise<DetalleCierreLoterias> {
  return fetchAPI<DetalleCierreLoterias>(`/caja-loteria/cierre/${cierreId}`);
}

export async function anularCierreLoterias(
  cierreId: string,
  motivo: string
): Promise<{ ok: boolean; message: string }> {
  return fetchAPI<{ ok: boolean; message: string }>(
    `/caja-loteria/cierre/${cierreId}/anular`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motivo }),
    }
  );
}

// ========================
// Cierres de Caja (CierreVendedor) — supervisión LOCAL
// ========================

export interface CierreCajaHistorico {
  id: string;
  vendedor_id: string;
  local_id: string;
  fecha: string;
  total_ingresos: string;
  total_egresos: string;
  saldo_teorico: string;
  efectivo_contado: string;
  diferencia: string;
  estado_diferencia: 'CUADRA' | 'SOBRA' | 'FALTA';
  anulado: boolean;
  created_at: string | null;
}

export async function getCierresCaja(
  vendedorId?: string,
  limit: number = 50,
  incluirAnulados: boolean = false,
): Promise<CierreCajaHistorico[]> {
  let url = `/caja-vendedor/cierres?limit=${limit}&incluir_anulados=${incluirAnulados}`;
  if (vendedorId) url += `&vendedor_id=${vendedorId}`;
  return fetchAPI<CierreCajaHistorico[]>(url);
}

export interface DetalleCierreVendedor extends CierreCajaHistorico {
  total_ventas_loteria: string;
  total_premios_pagados: string;
  total_credito_vendido: string;
  recargas_monto_inicial: string;
  recargas_monto_final: string;
  total_recargas: string;
  total_vales: string;
  total_ingresos_extra: string;
  total_loteria_fisica: string;
  total_pagos_digitales: string;
  notas: string | null;
  anulado_por: string | null;
  anulado_at: string | null;
  motivo_anulacion: string | null;
  vales: { id: string; monto: string; justificacion: string }[];
  ingresos_extra: { id: string; concepto: string; monto: string }[];
  loteria_fisica: { id: string; nombre: string; cantidad_inicial: number; cantidad_final: number; costo_unitario: string; total_vendido: string }[];
}

export async function getDetalleCierreVendedor(
  cierreId: string
): Promise<DetalleCierreVendedor> {
  return fetchAPI<DetalleCierreVendedor>(`/caja-vendedor/cierre/${cierreId}`);
}

export async function anularCierreVendedor(
  cierreId: string,
  motivo: string
): Promise<{ ok: boolean; message: string }> {
  return fetchAPI<{ ok: boolean; message: string }>(
    `/caja-vendedor/cierre/${cierreId}/anular`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motivo }),
    }
  );
}

export async function getEstadoCierreLoterias(
  vendedorId: string,
  fecha?: string
): Promise<EstadoCierreLoterias> {
  let url = `/caja-loteria/estado?vendedor_id=${vendedorId}`;
  if (fecha) url += `&fecha=${fecha}`;
  return fetchAPI<EstadoCierreLoterias>(url);
}
